import { useEffect } from "react";
import { NavigateOptions, useSearchParams } from "react-router-dom";

export type URLParamType = string | number | boolean | undefined | string[] | undefined[];

export type URLParamConverter<T> = {
  [K in keyof T]: (value: string | null) => T[K];
};

export const useURLParams = <T extends Record<string, URLParamType>>(
  defaultParams: T,
  converters: URLParamConverter<T>
) => {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (Array.from(searchParams.entries()).length === 0) {
      updateSearchParams(defaultParams, { replace: true });
    }
    // only apply one time
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const urlParams = {} as T;

  for (const key in defaultParams) {
    const paramValue = searchParams.get(key);
    urlParams[key] = converters[key](paramValue);
  }

  const updateSearchParams = (updatedParams: Partial<T>, options?: NavigateOptions) => {
    Object.entries(updatedParams).forEach(([key, value]) => {
      // remove unnecessary params
      if (
        value === undefined ||
        value === null ||
        value === "" ||
        (Array.isArray(value) && value.length === 0)
      ) {
        searchParams.delete(key);
      } else if (Array.isArray(value)) {
        // Handle string arrays
        searchParams.set(key, value.join(","));
      } else {
        searchParams.set(key, value.toString());
      }
    });

    setSearchParams(searchParams, options);
  };

  return {
    urlParams,
    updateSearchParams,
  };
};
